import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { productData } from '../../../mocks/products';

type Step = 'intro' | 'industry' | 'role' | 'workstyle' | 'priority' | 'result';

interface Answer {
  industry: string;
  role: string;
  workstyle: string;
  priority: string;
}

interface ModelResult {
  model: string;
  label: string;
  color: string;
  match: number;
  keyword: string;
  description: string;
  features: string[];
  targetUser: string;
  price: number;
  priceNote: string;
}

// Add new interface for diagnosis result
interface DiagnosisResult {
  model: string;
  score: number;
}

const modelImages: Record<string, string> = {
  Core: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/8f2f2f2c0f0f0f0f0f0f0f0f0f0f0f0f.png',
  Pro: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/9a3b3b3b0a0a0a0a0a0a0a0a0a0a0a0a.png',
  Style: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/7c4d4d4d0c0c0c0c0c0c0c0c0c0c0c0c.png',
};

const industries = [
  { id: 'it', label: 'IT・テクノロジー', icon: 'ri-code-s-slash-line', desc: 'ソフトウェア開発・SaaS・Web系' },
  { id: 'creative', label: 'クリエイティブ・デザイン', icon: 'ri-palette-line', desc: '広告・映像・デザイン事務所' },
  { id: 'finance', label: '金融・コンサルティング', icon: 'ri-line-chart-line', desc: '銀行・証券・会計・コンサル' },
  { id: 'manufacturing', label: 'メーカー・製造業', icon: 'ri-settings-3-line', desc: '製造・研究開発・品質管理' },
  { id: 'medical', label: '医療・ヘルスケア', icon: 'ri-heart-pulse-line', desc: '病院・クリニック・介護施設' },
  { id: 'general', label: 'その他一般企業', icon: 'ri-building-2-line', desc: '商社・不動産・サービス業など' },
];

const roles = [
  { id: 'executive', label: '経営者・役員', icon: 'ri-vip-crown-line', desc: '意思決定・戦略立案' },
  { id: 'manager', label: '管理職・マネージャー', icon: 'ri-team-line', desc: 'チーム管理・プロジェクト推進' },
  { id: 'engineer', label: 'エンジニア・開発者', icon: 'ri-terminal-box-line', desc: '長時間コーディング・技術業務' },
  { id: 'designer', label: 'デザイナー・クリエイター', icon: 'ri-brush-line', desc: 'デザイン・映像・コンテンツ制作' },
  { id: 'sales', label: '営業・企画', icon: 'ri-presentation-line', desc: '提案書作成・顧客対応' },
  { id: 'backoffice', label: '事務・バックオフィス', icon: 'ri-file-list-3-line', desc: '経理・人事・総務・一般事務' },
];

const workstyles = [
  { id: 'long_desk', label: '長時間デスクワーク中心', icon: 'ri-computer-line', desc: '1日6時間以上PCに向かう' },
  { id: 'standing_desk', label: '電動昇降デスクを使用', icon: 'ri-arrow-up-down-line', desc: '立ち座りを切り替えて作業' },
  { id: 'meeting_mix', label: 'デスク＋会議が半々', icon: 'ri-discuss-line', desc: 'デスクワークと会議室を行き来' },
  { id: 'health_focus', label: '健康・姿勢を重視したい', icon: 'ri-mental-health-line', desc: '腰痛予防や体幹強化に関心' },
];

const priorities = [
  { id: 'cost', label: 'コストパフォーマンス', icon: 'ri-money-yen-circle-line', desc: '品質と価格のバランスを重視' },
  { id: 'performance', label: '最高のパフォーマンス', icon: 'ri-rocket-line', desc: '機能性・品質に妥協しない' },
  { id: 'health', label: '健康・ウェルビーイング', icon: 'ri-leaf-line', desc: '身体への負担軽減を最優先' },
  { id: 'design', label: 'デザイン・空間との調和', icon: 'ri-layout-masonry-line', desc: 'オフィスの見た目も大切' },
];

function calculateResult(answers: Answer): ModelResult[] {
  let coreScore = 0;
  let proScore = 0;
  let styleScore = 0;

  // Industry scoring
  switch (answers.industry) {
    case 'it': proScore += 3; coreScore += 1; break;
    case 'creative': proScore += 4; break;
    case 'finance': coreScore += 2; proScore += 2; break;
    case 'manufacturing': coreScore += 3; proScore += 1; break;
    case 'medical': styleScore += 3; coreScore += 1; break;
    case 'general': coreScore += 3; styleScore += 1; break;
  }

  // Role scoring
  switch (answers.role) {
    case 'executive': proScore += 3; coreScore += 1; break;
    case 'manager': coreScore += 2; proScore += 2; break;
    case 'engineer': proScore += 4; break;
    case 'designer': proScore += 4; break;
    case 'sales': coreScore += 2; styleScore += 2; break;
    case 'backoffice': coreScore += 4; break;
  }

  // Workstyle scoring
  switch (answers.workstyle) {
    case 'long_desk': proScore += 3; coreScore += 1; break;
    case 'standing_desk': proScore += 4; break;
    case 'meeting_mix': coreScore += 3; styleScore += 1; break;
    case 'health_focus': styleScore += 4; break;
  }

  // Priority scoring
  switch (answers.priority) {
    case 'cost': coreScore += 4; break;
    case 'performance': proScore += 4; break;
    case 'health': styleScore += 4; break;
    case 'design': coreScore += 2; styleScore += 2; break;
  }

  const total = coreScore + proScore + styleScore;
  const coreMatch = Math.round((coreScore / total) * 100);
  const proMatch = Math.round((proScore / total) * 100);
  const styleMatch = Math.round((styleScore / total) * 100);

  const results: ModelResult[] = [
    {
      model: 'Core',
      label: '集中',
      color: '#4a5568',
      match: coreMatch,
      keyword: '集中力を最大化',
      description: '一般企業オフィスにちょうど良いルックスと高機能をリーズナブルに。標準デスクワークの快適性を最大化し、社員の集中力を引き出します。',
      features: ['標準アームレスト', '快適メッシュバック', 'コストパフォーマンス重視', '標準デスク環境に最適化'],
      targetUser: '一般企業オフィス / バックオフィス / 標準デスクワーク',
      price: 49800,
      priceNote: '税込・送料別',
    },
    {
      model: 'Pro',
      label: '創造性',
      color: '#2c4a6e',
      match: proMatch,
      keyword: '創造性を解放',
      description: '6Dアームレストが電動昇降デスクとの相性抜群。クオリティを妥協したくないクリエイターやビジネスマンの創造性を最大限に引き出します。',
      features: ['6Dアームレスト搭載', '電動昇降デスク対応', '高剛性フレーム', '長時間作業でも疲労最小化'],
      targetUser: 'クリエイター / エンジニア / ビジネスマン',
      price: 89800,
      priceNote: '税込・送料別',
    },
    {
      model: 'Style',
      label: '回復力',
      color: '#3d6b4a',
      match: styleMatch,
      keyword: '回復力を高める',
      description: 'バランスボール代替品として、体幹を自然に刺激。健康志向ユーザーの回復力を高め、職場全体のウェルビーイングを底上げします。',
      features: ['体幹刺激座面設計', 'バランスボール効果', 'ナチュラルフィット', '姿勢改善サポート'],
      targetUser: '健康志向ユーザー / ウェルネス重視',
      price: 69800,
      priceNote: '税込・送料別',
    },
  ];

  results.sort((a, b) => b.match - a.match);
  return results;
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const progress = (current / total) * 100;
  return (
    <div className="mb-10">
      <div className="flex justify-between items-center mb-3">
        <span className="text-xs font-medium text-[#4a4a4a]">ステップ {current} / {total}</span>
        <span className="text-xs font-medium text-[#4a4a4a]">{Math.round(progress)}%</span>
      </div>
      <div className="w-full h-2 bg-[#e8e6e3] rounded-full overflow-hidden">
        <div
          className="h-full bg-[#1a2332] rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function OptionCard({
  icon,
  label,
  desc,
  selected,
  onClick,
  delay,
}: {
  icon: string;
  label: string;
  desc: string;
  selected: boolean;
  onClick: () => void;
  delay: number;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-300 cursor-pointer flex items-center gap-4 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      } ${
        selected
          ? 'border-[#1a2332] bg-[#1a2332]/5 shadow-md'
          : 'border-[#e8e6e3] bg-white hover:border-[#1a2332]/40 hover:shadow-sm'
      }`}
    >
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
          selected ? 'bg-[#1a2332] text-white' : 'bg-[#f5f3ef] text-[#1a2332]'
        }`}
      >
        <i className={`${icon} text-xl`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-[#1a2332]">{label}</div>
        <div className="text-xs text-[#4a4a4a] mt-0.5">{desc}</div>
      </div>
      {selected && (
        <div className="w-6 h-6 rounded-full bg-[#1a2332] flex items-center justify-center flex-shrink-0">
          <i className="ri-check-line text-sm text-white"></i>
        </div>
      )}
    </button>
  );
}

function ResultCard({ result, rank }: { result: DiagnosisResult; rank: number }) {
  const navigate = useNavigate();
  const modelKey = result.model.toLowerCase();
  const product = productData[modelKey];

  return (
    <div
      className={`bg-white rounded-2xl p-6 shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${
        rank === 0 ? 'border-[#e07856]' : 'border-transparent'
      }`}
      style={{ animationDelay: `${rank * 150}ms` }}
    >
      {rank === 0 && (
        <div className="inline-flex items-center gap-2 bg-[#e07856] text-white px-3 py-1 rounded-full text-xs font-bold mb-4">
          <i className="ri-award-line text-sm"></i>
          BEST MATCH
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-6">
        {/* Product Image */}
        <div className="w-full sm:w-40 h-40 flex-shrink-0 rounded-xl flex items-center justify-center overflow-hidden" style={{ backgroundColor: `${product.color}08` }}>
          <img
            src={product.heroImage}
            alt={product.fullName}
            title={product.fullName}
            className="w-full h-full object-contain"
          />
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <span
              className="text-xs font-bold px-3 py-1 rounded-full text-white"
              style={{ backgroundColor: product.color }}
            >
              {product.label}
            </span>
            <div className="flex items-center gap-1">
              <div className="text-2xl font-bold" style={{ color: product.color }}>
                {result.score}%
              </div>
              <div className="text-xs text-[#4a4a4a]">マッチ度</div>
            </div>
          </div>

          <h4 className="text-xl font-bold text-[#1a2332] mb-2">{product.fullName}</h4>

          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-2xl font-bold" style={{ color: product.color }}>
              ¥{product.price.toLocaleString()}
            </span>
            <span className="text-xs text-[#4a4a4a]">{product.priceNote}</span>
          </div>

          <p className="text-sm text-[#4a4a4a] mb-4 leading-relaxed">{product.description}</p>

          <div className="flex flex-wrap gap-2 mb-4">
            {product.highlights.slice(0, 3).map((h, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-[#4a4a4a]">
                <i className="ri-check-line text-sm" style={{ color: product.color }}></i>
                <span>{h}</span>
              </div>
            ))}
          </div>

          <div className="bg-[#f5f3ef] rounded-lg p-3 mb-4">
            <div className="text-xs font-bold text-[#1a2332] mb-1">想定ユーザー</div>
            <div className="text-xs text-[#4a4a4a]">{product.targetUser}</div>
          </div>

          <button
            onClick={() => {
              navigate(`/product/${modelKey}`);
              window.scrollTo(0, 0);
            }}
            className="px-6 py-2.5 rounded-full text-white text-sm font-bold hover:opacity-90 transition-all inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
            style={{ backgroundColor: product.color }}
          >
            <span>詳細を見る</span>
            <i className="ri-arrow-right-line text-base"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ModelDiagnosis() {
  const [step, setStep] = useState<Step>('intro');
  const [answers, setAnswers] = useState<Answer>({
    industry: '',
    role: '',
    workstyle: '',
    priority: '',
  });
  const [results, setResults] = useState<DiagnosisResult[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  const scrollToTop = () => {
    if (sectionRef.current) {
      const offset = sectionRef.current.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  };

  const handleSelect = (field: keyof Answer, value: string) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    const stepOrder: Step[] = ['intro', 'industry', 'role', 'workstyle', 'priority', 'result'];
    const currentIndex = stepOrder.indexOf(step);
    const next = stepOrder[currentIndex + 1];
    if (next === 'result') {
      const calculatedResults = calculateResult(answers);
      const diagnosisResults: DiagnosisResult[] = calculatedResults.map(r => ({
        model: r.model,
        score: r.match
      }));
      setResults(diagnosisResults);
    }
    setStep(next);
    setTimeout(scrollToTop, 100);
  };

  const prevStep = () => {
    const stepOrder: Step[] = ['intro', 'industry', 'role', 'workstyle', 'priority', 'result'];
    const currentIndex = stepOrder.indexOf(step);
    setStep(stepOrder[currentIndex - 1]);
    setTimeout(scrollToTop, 100);
  };

  const resetDiagnosis = () => {
    setStep('intro');
    setAnswers({ industry: '', role: '', workstyle: '', priority: '' });
    setResults([]);
    setTimeout(scrollToTop, 100);
  };

  const canProceed = () => {
    switch (step) {
      case 'industry': return answers.industry !== '';
      case 'role': return answers.role !== '';
      case 'workstyle': return answers.workstyle !== '';
      case 'priority': return answers.priority !== '';
      default: return true;
    }
  };

  const stepNumber = () => {
    const map: Record<string, number> = { industry: 1, role: 2, workstyle: 3, priority: 4 };
    return map[step] || 0;
  };

  const scrollToContact = () => {
    const el = document.getElementById('contact-form');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={sectionRef} id="diagnosis" className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6">

        {/* Intro */}
        {step === 'intro' && (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-[#e07856]/10 text-[#e07856] px-4 py-2 rounded-full text-sm font-bold mb-8">
              <i className="ri-stethoscope-line text-lg"></i>
              無料モデル診断
            </div>
            <h2 className="text-4xl font-bold mb-4 text-[#1a2332] leading-tight" style={{ fontFamily: 'Noto Serif JP, serif' }}>
              あなたの業種・役職に<br />最適なモデルを診断
            </h2>
            <p className="text-base text-[#4a4a4a] mb-10 leading-relaxed">
              4つの質問に答えるだけで、Core・Pro・Styleの中から<br />
              あなたに最適なMeVER Chairモデルをご提案します。
            </p>

            {/* 3 Model Preview */}
            <div className="grid grid-cols-3 gap-4 mb-10">
              <div className="bg-[#f5f3ef] rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-[#4a5568] flex items-center justify-center mx-auto mb-3">
                  <i className="ri-focus-3-line text-lg text-white"></i>
                </div>
                <div className="text-sm font-bold text-[#1a2332]">Core</div>
                <div className="text-xs text-[#4a4a4a] mt-1">集中力を最大化</div>
              </div>
              <div className="bg-[#f5f3ef] rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-[#2c4a6e] flex items-center justify-center mx-auto mb-3">
                  <i className="ri-lightbulb-flash-line text-lg text-white"></i>
                </div>
                <div className="text-sm font-bold text-[#1a2332]">Pro</div>
                <div className="text-xs text-[#4a4a4a] mt-1">創造性を解放</div>
              </div>
              <div className="bg-[#f5f3ef] rounded-xl p-5">
                <div className="w-10 h-10 rounded-full bg-[#3d6b4a] flex items-center justify-center mx-auto mb-3">
                  <i className="ri-heart-pulse-line text-lg text-white"></i>
                </div>
                <div className="text-sm font-bold text-[#1a2332]">Style</div>
                <div className="text-xs text-[#4a4a4a] mt-1">回復力を高める</div>
              </div>
            </div>

            <button
              onClick={nextStep}
              className="bg-[#1a2332] text-white px-10 py-4 rounded-full text-base font-bold hover:bg-[#2c4a6e] transition-all inline-flex items-center gap-3 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <span>診断をはじめる（約1分）</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
            <p className="text-xs text-[#4a4a4a] mt-4">個人情報の入力は不要です</p>
          </div>
        )}

        {/* Question Steps */}
        {(step === 'industry' || step === 'role' || step === 'workstyle' || step === 'priority') && (
          <div>
            <ProgressBar current={stepNumber()} total={4} />

            {step === 'industry' && (
              <>
                <div className="mb-8">
                  <span className="inline-block bg-[#1a2332] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">Q1</span>
                  <h3 className="text-2xl font-bold text-[#1a2332]">あなたの業種を教えてください</h3>
                  <p className="text-sm text-[#4a4a4a] mt-2">最も近いものをお選びください</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {industries.map((item, i) => (
                    <OptionCard
                      key={item.id}
                      icon={item.icon}
                      label={item.label}
                      desc={item.desc}
                      selected={answers.industry === item.id}
                      onClick={() => handleSelect('industry', item.id)}
                      delay={i * 80}
                    />
                  ))}
                </div>
              </>
            )}

            {step === 'role' && (
              <>
                <div className="mb-8">
                  <span className="inline-block bg-[#1a2332] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">Q2</span>
                  <h3 className="text-2xl font-bold text-[#1a2332]">あなたの役職を教えてください</h3>
                  <p className="text-sm text-[#4a4a4a] mt-2">主な業務内容に近いものをお選びください</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {roles.map((item, i) => (
                    <OptionCard
                      key={item.id}
                      icon={item.icon}
                      label={item.label}
                      desc={item.desc}
                      selected={answers.role === item.id}
                      onClick={() => handleSelect('role', item.id)}
                      delay={i * 80}
                    />
                  ))}
                </div>
              </>
            )}

            {step === 'workstyle' && (
              <>
                <div className="mb-8">
                  <span className="inline-block bg-[#1a2332] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">Q3</span>
                  <h3 className="text-2xl font-bold text-[#1a2332]">普段のワークスタイルは？</h3>
                  <p className="text-sm text-[#4a4a4a] mt-2">最も当てはまるものをお選びください</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {workstyles.map((item, i) => (
                    <OptionCard
                      key={item.id}
                      icon={item.icon}
                      label={item.label}
                      desc={item.desc}
                      selected={answers.workstyle === item.id}
                      onClick={() => handleSelect('workstyle', item.id)}
                      delay={i * 80}
                    />
                  ))}
                </div>
              </>
            )}

            {step === 'priority' && (
              <>
                <div className="mb-8">
                  <span className="inline-block bg-[#1a2332] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">Q4</span>
                  <h3 className="text-2xl font-bold text-[#1a2332]">チェア選びで最も重視するポイントは？</h3>
                  <p className="text-sm text-[#4a4a4a] mt-2">1つお選びください</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {priorities.map((item, i) => (
                    <OptionCard
                      key={item.id}
                      icon={item.icon}
                      label={item.label}
                      desc={item.desc}
                      selected={answers.priority === item.id}
                      onClick={() => handleSelect('priority', item.id)}
                      delay={i * 80}
                    />
                  ))}
                </div>
              </>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-10">
              <button
                onClick={prevStep}
                className="text-sm text-[#4a4a4a] hover:text-[#1a2332] transition-colors flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
                戻る
              </button>
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className="bg-[#1a2332] text-white px-8 py-3 rounded-full text-sm font-bold hover:bg-[#2c4a6e] transition-all flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                <span>{step === 'priority' ? '診断結果を見る' : '次へ'}</span>
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {step === 'result' && results.length > 0 && (
          <div>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-[#e07856]/10 text-[#e07856] px-4 py-2 rounded-full text-sm font-bold mb-6">
                <i className="ri-checkbox-circle-line text-lg"></i>
                診断完了
              </div>
              <h3 className="text-3xl font-bold text-[#1a2332] mb-2" style={{ fontFamily: 'Noto Serif JP, serif' }}>
                あなたにおすすめのモデル
              </h3>
              <p className="text-sm text-[#4a4a4a]">回答内容をもとに、最適なモデルを算出しました</p>
            </div>

            {/* Diagnosis Basis */}
            <div className="bg-[#f5f3ef] rounded-xl p-5 mb-8">
              <div className="text-xs font-bold text-[#1a2332] mb-3">あなたの回答</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-xs text-[#4a4a4a] mb-1">業種</div>
                  <div className="text-xs font-bold text-[#1a2332]">
                    {industries.find((i) => i.id === answers.industry)?.label}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-xs text-[#4a4a4a] mb-1">役職</div>
                  <div className="text-xs font-bold text-[#1a2332]">
                    {roles.find((r) => r.id === answers.role)?.label}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-xs text-[#4a4a4a] mb-1">ワークスタイル</div>
                  <div className="text-xs font-bold text-[#1a2332]">
                    {workstyles.find((w) => w.id === answers.workstyle)?.label}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-xs text-[#4a4a4a] mb-1">重視ポイント</div>
                  <div className="text-xs font-bold text-[#1a2332]">
                    {priorities.find((p) => p.id === answers.priority)?.label}
                  </div>
                </div>
              </div>
            </div>

            {/* Result Cards */}
            <div className="space-y-4 mb-10">
              {results.map((result, i) => (
                <ResultCard key={result.model} result={result} rank={i} />
              ))}
            </div>

            {/* CTA */}
            <div className="bg-[#1a2332] rounded-2xl p-8 text-center text-white">
              <h4 className="text-xl font-bold mb-2">診断結果をもとにご提案いたします</h4>
              <p className="text-sm text-white/70 mb-6">
                部署・フロア単位での混在導入も可能です。お気軽にご相談ください。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={scrollToContact}
                  className="bg-white text-[#1a2332] px-8 py-3 rounded-full text-sm font-bold hover:opacity-90 transition-all inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <span>無料見積もりを依頼する</span>
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
                <button
                  onClick={resetDiagnosis}
                  className="text-white/70 hover:text-white text-sm transition-colors inline-flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-base"></i>
                  もう一度診断する
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
